import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ServicePageHeader } from "@/components/ServiceHistoryButton";
import { AdminCost, useSourceCosts } from "@/components/AdminSourceCost";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { ReceiptText, ExternalLink, RefreshCw, CheckCircle2 } from "lucide-react";
import { DocumentAutofill, mapDocToAnagrafica } from "@/components/DocumentAutofill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { avviso_pa: "", ente_pa: "", importo: "", email: "", nome: "", cognome: "", telefono: "" };

export default function Bollettini() {
  const [fee, setFee] = useState(2.0);
  const { costs: sourceCosts } = useSourceCosts();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState(null); // { provider_id, payment_url }
  const [status, setStatus] = useState(null);
  const [verificationError, setVerificationError] = useState("");
  const [checking, setChecking] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const pickCustomer = (id) => {
    setCustomerId(id);
    const c = customers.find((x) => x.id === id); if (!c) return;
    const parts = (c.name || "").trim().split(/\s+/);
    setForm((f) => ({ ...f, nome: (c.admin?.nome || parts[0]) || f.nome, cognome: (c.admin?.cognome || parts.slice(1).join(" ")) || f.cognome, email: c.email || f.email }));
  };
  const [requireDoc, setRequireDoc] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setRequireDoc(!!r.data.require_doc_bollettini)).catch(() => {}); }, []);
  const onDocData = (d) => { const a = mapDocToAnagrafica(d); setForm((f) => ({ ...f, nome: a.nome || f.nome, cognome: a.cognome || f.cognome })); };

  useEffect(() => {
    api.get("/products", { params: { category: "servizio" } }).then((r) => {
      const p = (r.data || []).find((x) => x.openapi_service_id === "oa_pagopa");
      if (p && p.price != null) setFee(Number(p.price));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (requireDoc && !docUploaded) { toast.error("Carica il documento del cliente per procedere"); return; }
    if (!form.avviso_pa.trim() || !form.ente_pa.trim() || !form.email.trim() || !(parseFloat(form.importo) > 0)) {
      toast.error("Compila codice avviso, ente/CBILL, importo ed email"); return;
    }
    setBusy(true); setStatus(null); setVerificationError("");
    try {
      const { data } = await api.post("/openapi/bollettini/pay", {
        email: form.email.trim(), importo: parseFloat(form.importo),
        ente_pa: form.ente_pa.trim(), avviso_pa: form.avviso_pa.trim(),
        nome: form.nome || undefined, cognome: form.cognome || undefined, telefono: form.telefono || undefined,
      });
      setPayment(data);
      toast.success(`Richiesta creata. Commissione ${euro(data.fee)} scalata dal Plafond servizi.`);
      if (data.payment_url) window.open(data.payment_url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nella creazione del pagamento");
    } finally { setBusy(false); }
  };

  const checkStatus = async () => {
    if (!payment?.provider_id) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/openapi/bollettini/${payment.provider_id}/status`);
      setStatus(data); setVerificationError("");
      data.status === "paid" ? toast.success("Pagamento completato!") : toast.info(data.notice || "Report finale non ancora disponibile.");
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita. Ultimo stato valido conservato: non ripetere il pagamento.";
      setVerificationError(message); toast.error(message);
    } finally { setChecking(false); }
  };

  const reset = () => { setPayment(null); setStatus(null); setVerificationError(""); setForm(EMPTY); };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="bollettini-page">
      <ServicePageHeader service="bollettini">
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi pubblici</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Pagamento Bollettini / pagoPA</h1>
        <p className="text-sm text-muted-foreground mt-1">Genera il collegamento per pagare un avviso pagoPA. Commissione servizio: <strong>{euro(fee)}</strong> <AdminCost cost={sourceCosts?.openapi?.oa_pagopa} testId="bollettini-source-cost" />, scalata alla creazione del collegamento, prima dell’esito del pagamento dell’avviso.</p>
      </ServicePageHeader>


      {!payment && (
        <Card className="p-5 glow-border space-y-3" data-testid="bollettino-form-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><ReceiptText className="w-4 h-4" /></div>
            <div className="mr-auto"><h2 className="font-head text-base font-semibold">Nuovo pagamento pagoPA</h2><p className="text-xs text-muted-foreground">Inserisci i dati riportati sull'avviso pagoPA.</p></div>
            <Select value={customerId} onValueChange={pickCustomer}>
              <SelectTrigger className="w-full sm:w-72" aria-label="Cliente registrato" data-testid="boll-customer-select"><SelectValue placeholder="Cliente salvato (opzionale)" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` — ${c.email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DocumentAutofill tipoCliente="privato" required={requireDoc} onData={onDocData} onUploadedChange={setDocUploaded} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2.5">
            <div className="lg:col-span-2"><Label>Codice avviso (IUV) *</Label><Input className="mt-1 font-mono" data-testid="boll-avviso-input" value={form.avviso_pa} onChange={set("avviso_pa")} placeholder="es. 312345678901234567" /></div>
            <div><Label>Ente / CBILL (C.F. ente) *</Label><Input className="mt-1 font-mono" data-testid="boll-ente-input" value={form.ente_pa} onChange={set("ente_pa")} placeholder="es. 01234567890" /></div>
            <div><Label>Importo (€) *</Label><Input type="number" step="0.01" min="0.01" max="2496.99" className="mt-1" data-testid="boll-importo-input" value={form.importo} onChange={set("importo")} placeholder="es. 87.50" /></div>
            <div><Label>Nome</Label><Input className="mt-1" data-testid="boll-nome-input" value={form.nome} onChange={set("nome")} /></div>
            <div><Label>Cognome</Label><Input className="mt-1" data-testid="boll-cognome-input" value={form.cognome} onChange={set("cognome")} /></div>
            <div><Label>Email per ricevuta *</Label><Input type="email" className="mt-1" data-testid="boll-email-input" value={form.email} onChange={set("email")} placeholder="cliente@email.it" /></div>
            <div><Label>Telefono</Label><Input type="tel" className="mt-1" data-testid="boll-telefono-input" value={form.telefono} onChange={set("telefono")} placeholder="+39…" /></div>
          </div>
          <div className="flex justify-end">
          <ConfirmButton onConfirm={submit} disabled={busy || (requireDoc && !docUploaded) || !form.avviso_pa || !form.ente_pa || !(Number(form.importo) > 0) || !/\S+@\S+\.\S+/.test(form.email || "")} className="gap-2 max-w-full h-auto min-h-10 whitespace-normal" data-testid="boll-submit-button"
            title="Confermi la creazione del pagamento?"
            description={`Verrà creato un link per l’avviso di € ${form.importo || "—"}. La commissione ${euro(fee)} viene scalata subito alla creazione del link, non alla conferma del pagamento. Non è previsto un rimborso automatico se il pagamento esterno non viene completato. Non procedere se hai già tentato di pagare questo avviso.`}
            confirmText="Crea pagamento">
            <ReceiptText className="w-4 h-4" /> {busy ? "Creazione…" : `Crea pagamento (commissione ${euro(fee)})`}
          </ConfirmButton>
          </div>
        </Card>
      )}

      {payment && (
        <Card className="p-6 glow-border space-y-4" data-testid="bollettino-result-card">
          <div className="flex items-center gap-2">
            <h2 className="font-head text-lg font-semibold">Richiesta di pagamento creata</h2>
            <Badge className={verificationError ? "bg-red-600 ml-auto" : status?.status === "paid" ? "bg-teal-600 hover:bg-teal-600 ml-auto" : "bg-amber-500 ml-auto"} data-testid="boll-status-badge">
              {verificationError ? "Verifica non riuscita" : status?.status === "paid" ? "Pagato" : status?.status === "needs_review" ? "Esito da verificare" : status?.status === "pending" ? "Report finale non disponibile" : "Pagamento non ancora verificato"}
            </Badge>
          </div>
          {verificationError && <div role="alert" className="rounded-lg border border-destructive/40 p-3 text-sm text-destructive" data-testid="boll-verification-error">{verificationError}{status && <p className="mt-2" data-testid="boll-last-valid-status">Ultima verifica valida: {status.status === "paid" ? "pagato" : status.status === "pending" ? "report finale non disponibile" : "esito da verificare"}.</p>}</div>}
          {!verificationError && status?.notice && <p className="text-sm text-muted-foreground" role="status" data-testid="boll-verification-notice">{status.notice}</p>}
          <p className="text-sm text-muted-foreground">ID transazione: <span className="font-mono">{payment.provider_id}</span></p>
          {payment.payment_url && !verificationError && status?.status !== "needs_review" && status?.status !== "paid" && (
            <Button asChild className="gap-2" data-testid="boll-open-payment"><a href={payment.payment_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /> Apri pagina di pagamento</a></Button>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={checkStatus} disabled={checking} className="gap-2" data-testid="boll-check-status"><RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} /> Verifica stato</Button>
            {status?.status === "paid" && status?.receipt_url && (
              <Button asChild variant="outline" className="gap-2" data-testid="boll-receipt"><a href={status.receipt_url} target="_blank" rel="noopener noreferrer"><CheckCircle2 className="w-4 h-4 text-teal-500" /> Scarica ricevuta</a></Button>
            )}
            <Button variant="ghost" onClick={reset} data-testid="boll-new">Nuovo bollettino</Button>
          </div>
          <p className="text-xs text-muted-foreground">Il pagamento dell’avviso avviene sul sito esterno. «Verifica stato» legge l’esito senza pagare di nuovo e non avvia rimborsi. Se hai già tentato il pagamento, verifica prima di aprire un nuovo collegamento.</p>
        </Card>
      )}
    </div>
  );
}
